import { useEffect, useRef } from 'react';

// 固定のレイアウト（毎回同じ構図になるよう、乱数は使わずあらかじめ配置を決めている）。
// x, y は画面比率(0-1)、len/wid は画面の短辺に対する比率。
const LAYERS = [
  { // 奥：大きくぼかした光だまり
    blur: 44, alphaMul: .5, tone: '--leaf-far',
    leaves: [
      { x: .06, y: .12, len: .52, wid: .30, rot: -24 },
      { x: .92, y: .08, len: .46, wid: .26, rot:  30 },
      { x: .88, y: .86, len: .58, wid: .32, rot: -40 },
      { x: .10, y: .90, len: .50, wid: .28, rot:  20 },
    ],
  },
  { // 中景
    blur: 18, alphaMul: .8, tone: '--leaf-mid',
    leaves: [
      { x: .03, y: .30, len: .34, wid: .16, rot: -12, kind: 'frond' },
      { x: .97, y: .34, len: .30, wid: .14, rot:  16, kind: 'frond' },
      { x: .80, y: .06, len: .26, wid: .13, rot:  46 },
      { x: .18, y: .04, len: .24, wid: .12, rot: -34 },
      { x: .95, y: .74, len: .30, wid: .15, rot: -20, kind: 'frond' },
    ],
  },
  { // 手前：くっきりした葉でフレームをつくる
    blur: 3, alphaMul: 1, tone: '--leaf-near',
    leaves: [
      { x: -.01, y: 1.02, len: .40, wid: .18, rot: 52 },
      { x:  .07, y: 1.04, len: .30, wid: .14, rot: 78, kind: 'frond' },
      { x: 1.01, y: -.01, len: .36, wid: .17, rot: -128 },
      { x:  .95, y: .03, len: .26, wid: .13, rot: -95, kind: 'frond' },
    ],
  },
];

function leafPath(len, wid) {
  const p = new Path2D();
  p.moveTo(0, -len / 2);
  p.bezierCurveTo(wid / 2, -len / 4, wid / 2, len / 4, 0, len / 2);
  p.bezierCurveTo(-wid / 2, len / 4, -wid / 2, -len / 4, 0, -len / 2);
  p.closePath();
  return p;
}

function frondPath(len, wid) {
  // 数枚の小さな葉を軸に沿って互い違いに配置した、シダのような房。
  const p = new Path2D();
  const n = 5;
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const py = -len / 2 + t * len;
    const scale = 1 - Math.abs(t - 0.5) * 0.9;
    const side = i % 2 === 0 ? 1 : -1;
    const px = side * wid * 0.32 * (0.4 + t * 0.6);
    const lw = wid * 0.42 * scale;
    const ll = len * 0.30 * scale;
    p.moveTo(px, py - ll / 2);
    p.bezierCurveTo(px + lw / 2, py - ll / 4, px + lw / 2, py + ll / 4, px, py + ll / 2);
    p.bezierCurveTo(px - lw / 2, py + ll / 4, px - lw / 2, py - ll / 4, px, py - ll / 2);
  }
  p.moveTo(0, -len / 2); p.lineTo(0, len / 2);
  return p;
}

function palette() {
  const cs = getComputedStyle(document.documentElement);
  return {
    '--leaf-far': cs.getPropertyValue('--leaf-far').trim(),
    '--leaf-mid': cs.getPropertyValue('--leaf-mid').trim(),
    '--leaf-near': cs.getPropertyValue('--leaf-near').trim(),
  };
}

/**
 * ログイン画面・ホーム画面で共有する、温室の背景（奥行き3層の葉＋ときどき流れる落ち葉）。
 * 画面のどこに置いても同じ見た目になるよう、常に position:fixed で全画面に敷く。
 */
export default function GreenhouseBackground() {
  const foliageRef = useRef(null);
  const fallingRef = useRef(null);

  useEffect(() => {
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canvas = foliageRef.current;
    const ctx = canvas.getContext('2d');
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0;
    const parallax = { x: 0, y: 0 };

    function draw() {
      const colors = palette();
      const short = Math.min(W, H);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      LAYERS.forEach((layer, li) => {
        const depth = (li + 1) / LAYERS.length; // 奥ほど小さい値
        const px = parallax.x * (0.5 - depth) * 26;
        const py = parallax.y * (0.5 - depth) * 18;
        ctx.save();
        ctx.filter = layer.blur ? `blur(${layer.blur}px)` : 'none';
        ctx.globalAlpha = layer.alphaMul;
        ctx.fillStyle = colors[layer.tone];
        layer.leaves.forEach((leaf) => {
          ctx.save();
          ctx.translate(leaf.x * W + px, leaf.y * H + py);
          ctx.rotate((leaf.rot * Math.PI) / 180);
          const len = leaf.len * short, wid = leaf.wid * short;
          const path = leaf.kind === 'frond' ? frondPath(len, wid) : leafPath(len, wid);
          ctx.fill(path);
          ctx.restore();
        });
        ctx.restore();
      });
    }

    function resize() {
      W = window.innerWidth; H = window.innerHeight;
      canvas.width = W * dpr; canvas.height = H * dpr;
      canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
      draw();
    }

    window.addEventListener('resize', resize);

    let onPointerMove = null;
    if (!reduceMotion && matchMedia('(pointer: fine)').matches) {
      onPointerMove = (e) => {
        parallax.x = e.clientX / W - 0.5;
        parallax.y = e.clientY / H - 0.5;
        requestAnimationFrame(draw);
      };
      window.addEventListener('pointermove', onPointerMove);
    }

    const darkSchemeQuery = matchMedia('(prefers-color-scheme: dark)');
    darkSchemeQuery.addEventListener('change', draw);
    const themeObserver = new MutationObserver(draw);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    resize();

    // ---- ときどき、葉が一枚だけ静かに上から下へ流れていく（控えめな演出。常時ではない） ----
    let fallTimer = null;
    let fallRaf = null;
    if (!reduceMotion) {
      const leafCanvas = fallingRef.current;
      const lctx = leafCanvas.getContext('2d');
      let lw = 0, lh = 0;
      const ldpr = Math.min(window.devicePixelRatio || 1, 2);

      const resizeLeafCanvas = () => {
        lw = window.innerWidth; lh = window.innerHeight;
        leafCanvas.width = lw * ldpr; leafCanvas.height = lh * ldpr;
        leafCanvas.style.width = lw + 'px'; leafCanvas.style.height = lh + 'px';
      };
      window.addEventListener('resize', resizeLeafCanvas);
      resizeLeafCanvas();

      const fallOnce = () => {
        const startX = lw * (0.18 + Math.random() * 0.64);
        const duration = 12000 + Math.random() * 5000; // 12〜17秒かけて、ゆっくり
        const swayAmp = 24 + Math.random() * 20;
        const swayFreq = 1 + Math.random() * 0.5;
        const size = 34 + Math.random() * 12;
        const tone = Math.random() < 0.5 ? '--leaf-mid' : '--leaf-far';
        const blurPx = 9 + Math.random() * 6;
        const started = performance.now();

        const frame = (now) => {
          const t = (now - started) / duration;
          lctx.setTransform(ldpr, 0, 0, ldpr, 0, 0);
          lctx.clearRect(0, 0, lw, lh);
          if (t >= 1) { scheduleNext(); return; }

          const y = -50 + t * (lh + 100);
          const x = startX + Math.sin(t * Math.PI * 2 * swayFreq) * swayAmp;
          const rot = Math.sin(t * Math.PI * 2 * swayFreq * 0.7) * 22 + t * 30;
          const fadeIn = Math.min(t / 0.08, 1);
          const fadeOut = Math.min((1 - t) / 0.15, 1);

          const color = getComputedStyle(document.documentElement).getPropertyValue(tone).trim();
          lctx.save();
          lctx.filter = `blur(${blurPx}px)`;
          lctx.translate(x, y);
          lctx.rotate((rot * Math.PI) / 180);
          lctx.globalAlpha = 0.7 * fadeIn * fadeOut;
          lctx.fillStyle = color;
          lctx.fill(leafPath(size, size * 0.46));
          lctx.restore();

          fallRaf = requestAnimationFrame(frame);
        };
        fallRaf = requestAnimationFrame(frame);
      };

      function scheduleNext() {
        const wait = 15000 + Math.random() * 10000; // 次までは15〜25秒あける
        fallTimer = setTimeout(fallOnce, wait);
      }
      scheduleNext();

      return () => {
        window.removeEventListener('resize', resize);
        window.removeEventListener('resize', resizeLeafCanvas);
        if (onPointerMove) window.removeEventListener('pointermove', onPointerMove);
        darkSchemeQuery.removeEventListener('change', draw);
        themeObserver.disconnect();
        clearTimeout(fallTimer);
        cancelAnimationFrame(fallRaf);
      };
    }

    return () => {
      window.removeEventListener('resize', resize);
      if (onPointerMove) window.removeEventListener('pointermove', onPointerMove);
      darkSchemeQuery.removeEventListener('change', draw);
      themeObserver.disconnect();
    };
  }, []);

  return (
    <>
      <canvas ref={foliageRef} className="gh-foliage" aria-hidden="true" />
      <canvas ref={fallingRef} className="gh-falling-leaf" aria-hidden="true" />
      <div className="gh-vignette" />
    </>
  );
}
